import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice, type UnknownAction } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'
import type { SelectEntry } from '#components/Select'
import type { ReportModes } from '#features/Feedback/useReportMode'

const highlightPlaceholderValues = ['no', 'strike', 'overlay'] as const
type HighlightPlaceholderValue = (typeof highlightPlaceholderValues)[number]
export const useHighlightPlaceholderSelectItems = () => {
  const { t } = useTranslation('shared')
  return [
    { id: 'no', value: t('settings.highlight-placeholders.no', 'No highlighting') },
    { id: 'strike', value: t('settings.highlight-placeholders.strike', 'Strike-Through') },
    { id: 'overlay', value: t('settings.highlight-placeholders.overlay', 'Full overlay') },
  ] satisfies SelectEntry<HighlightPlaceholderValue>[]
}

const sidepanelsBehavior = ['switching', 'force-foldout', 'force-popover', 'disable'] as const
export type SidepanelsBehavior = (typeof sidepanelsBehavior)[number]
export const useSidepanelsSelectItems = () => {
  const { t } = useTranslation('shared')

  return [
    { id: 'switching', value: t('settings.sidepanels.switching', 'Automatically switch') },
    { id: 'force-foldout', value: t('settings.sidepanels.force-foldout', 'Force Foldouts') },
    { id: 'force-popover', value: t('settings.sidepanels.force-popover', 'Force Popover') },
    { id: 'disable', value: t('settings.sidepanels.disable', 'No Sidepanel folding') },
  ] satisfies SelectEntry<SidepanelsBehavior>[]
}

export type Theme = 'default' | 'dark' | 'high-contrast'
export const useTranslatedThemeSelectItems = () => {
  const { t } = useTranslation('shared')

  return [
    { id: 'default', value: t('settings.contrast-theme.default', 'Default') },
    { id: 'dark', value: t('settings.contrast-theme.dark', 'Dark') },
    { id: 'high-contrast', value: t('settings.contrast-theme.high-contrast', 'High Contrast') },
  ] satisfies SelectEntry<Theme>[]
}

export interface WelcomeOverlayState {
  lastDismissedVersion?: string
}

export interface ComponentConfigurationState {
  highlightPlaceholders: HighlightPlaceholderValue
  forceHoverPopupsOpen: boolean
  sidepanelBehavior: SidepanelsBehavior
  theme: Theme
  // move this to a separate shared user config slice
  reportMode?: ReportModes
  welcomeOverlay?: WelcomeOverlayState
}

const initialComponentConfigurationState: ComponentConfigurationState = {
  highlightPlaceholders: 'strike',
  forceHoverPopupsOpen: false,
  sidepanelBehavior: 'disable',
  theme: 'default',
  // move this to a separate shared user config slice
  reportMode: 'email',
  welcomeOverlay: {},
}

export const componentConfigurationSlice = createSlice({
  name: 'componentConfiguration',
  initialState: initialComponentConfigurationState,
  reducers: {
    setHighlightPlaceholders: (state, action: PayloadAction<HighlightPlaceholderValue>) => {
      state.highlightPlaceholders = action.payload
    },
    setForceHoverPopupsOpen: (state, action: PayloadAction<boolean>) => {
      state.forceHoverPopupsOpen = action.payload
    },
    setSidepanelsBehavior: (state, action: PayloadAction<SidepanelsBehavior>) => {
      state.sidepanelBehavior = action.payload
    },
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload
    },
    setReportMode: (state, action: PayloadAction<ReportModes | undefined>) => {
      state.reportMode = action.payload
    },
    setWelcomeOverlayDismissed: (state, action: PayloadAction<string | undefined>) => {
      state.welcomeOverlay = {
        ...state.welcomeOverlay,
        lastDismissedVersion: action.payload,
      }
    },
  },
})

const allActionsSet = new Set<string>(Object.values(componentConfigurationSlice.actions).map((action) => action.type))

export function isComponentConfigurationAction(action: UnknownAction): boolean {
  return allActionsSet.has(action.type)
}

export const { setHighlightPlaceholders, setForceHoverPopupsOpen, setTheme, setSidepanelsBehavior, setReportMode, setWelcomeOverlayDismissed } =
  componentConfigurationSlice.actions
export const componentConfigurationActions = componentConfigurationSlice.actions
